/**
 * Content for the Scheduler preview at /demo/schedule. The report names are
 * real ones from the Analytics Library, so a visitor can go and look at what
 * they just scheduled; the subjects and schedules are invented.
 */

export const SHIPPER_GUIDE_SCHEDULE_PROFILE = 'ShipperGuide'
export const OPENDOCK_SCHEDULE_PROFILE = 'Opendock'

export type DemoScheduleReport = {
  id: string
  profile: string
  reportName: string
}

export const DEMO_SCHEDULE_REPORTS: Array<DemoScheduleReport> = [
  { id: 'cost-leakage', profile: SHIPPER_GUIDE_SCHEDULE_PROFILE, reportName: 'Cost Leakage' },
  { id: 'purchasing', profile: SHIPPER_GUIDE_SCHEDULE_PROFILE, reportName: 'Purchasing' },
  {
    id: 'carrier-scorecard',
    profile: SHIPPER_GUIDE_SCHEDULE_PROFILE,
    reportName: 'Carrier Scorecard',
  },
  {
    id: 'accessorials-breakdown',
    profile: SHIPPER_GUIDE_SCHEDULE_PROFILE,
    reportName: 'Accessorials Breakdown',
  },
  {
    id: 'opendock-otd',
    profile: OPENDOCK_SCHEDULE_PROFILE,
    reportName: 'Opendock OTD & Logistics Times',
  },
  {
    id: 'opendock-capacity',
    profile: OPENDOCK_SCHEDULE_PROFILE,
    reportName: 'Opendock - Capacity Report',
  },
  {
    id: 'opendock-timeliness',
    profile: OPENDOCK_SCHEDULE_PROFILE,
    reportName: 'Opendock Timeliness of Appointments',
  },
]

export const DEMO_SCHEDULE_FREQUENCIES = ['Daily', 'Weekly', 'Monthly'] as const

export type DemoScheduleFrequency = (typeof DEMO_SCHEDULE_FREQUENCIES)[number]

export const DEMO_SCHEDULE_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Month days stop at 28 so a monthly schedule lands in every month.
export const DEMO_SCHEDULE_MONTH_DAYS = Array.from({ length: 28 }, (_, index) =>
  String(index + 1)
)

export const DEMO_SCHEDULE_TIMES = ['06:00', '07:00', '08:00', '12:00', '17:00']

// The same zone list the signed-in scheduler form offers.
export const DEMO_SCHEDULE_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
]

/**
 * `Preview sent` is the one-time send the live portal makes on creation;
 * recurring delivery is turned on by our team, so a new row is never `Active`.
 */
export type DemoScheduleStatus = 'Active' | 'Preview sent'

export type DemoSchedule = {
  id: string
  subject: string
  profile: string
  reportName: string
  recurrence: string
  status: DemoScheduleStatus
}

export type DemoScheduleDraft = {
  subject: string
  reportId: string
  frequency: DemoScheduleFrequency
  day: string
  time: string
  timezone: string
}

export const DEMO_SCHEDULE_DRAFT: DemoScheduleDraft = {
  subject: 'Monday freight digest',
  reportId: DEMO_SCHEDULE_REPORTS[0].id,
  frequency: 'Weekly',
  day: 'Mon',
  time: '07:00',
  timezone: 'America/New_York',
}

// Pre-filled, so the table explains the feature before anything is clicked.
export const DEMO_SCHEDULES: Array<DemoSchedule> = [
  {
    id: 'weekly-carrier-scorecard',
    subject: 'Weekly carrier scorecard',
    profile: SHIPPER_GUIDE_SCHEDULE_PROFILE,
    reportName: 'Carrier Scorecard',
    recurrence: 'Weekly - Mon at 07:00 (America/New_York)',
    status: 'Active',
  },
  {
    id: 'daily-dock-exceptions',
    subject: 'Daily dock exceptions',
    profile: OPENDOCK_SCHEDULE_PROFILE,
    reportName: 'Opendock Timeliness of Appointments',
    recurrence: 'Daily - at 06:00 (America/Chicago)',
    status: 'Active',
  },
  {
    id: 'monthly-accessorials',
    subject: 'Monthly accessorial review',
    profile: SHIPPER_GUIDE_SCHEDULE_PROFILE,
    reportName: 'Accessorials Breakdown',
    recurrence: 'Monthly - 1 at 08:00 (UTC)',
    status: 'Active',
  },
]

export function demoScheduleReport(reportId: string): DemoScheduleReport {
  return DEMO_SCHEDULE_REPORTS.find(report => report.id === reportId) ?? DEMO_SCHEDULE_REPORTS[0]
}

// Matches the signed-in format: `Weekly - Mon at 07:00 (America/New_York)`.
export function formatDemoRecurrence(draft: DemoScheduleDraft): string {
  const day = draft.frequency === 'Daily' ? '' : `${draft.day} `

  return `${draft.frequency} - ${day}at ${draft.time} (${draft.timezone})`
}

export function buildDemoSchedule(draft: DemoScheduleDraft, id: string): DemoSchedule {
  const report = demoScheduleReport(draft.reportId)

  return {
    id,
    subject: draft.subject.trim() || report.reportName,
    profile: report.profile,
    reportName: report.reportName,
    recurrence: formatDemoRecurrence(draft),
    status: 'Preview sent',
  }
}

export const DEMO_SCHEDULE_HIGHLIGHTS = [
  {
    title: 'The report comes to you',
    body: 'Pick a report from your library and a time. It arrives as an attachment you can forward, with no login and no export step between the data and the people who need it.',
  },
  {
    title: 'Daily, weekly or monthly',
    body: 'A daily exceptions digest for the team working the docks, a Monday scorecard for the carrier call, a first-of-the-month accessorial review for finance. Same data, three cadences.',
  },
  {
    title: 'Sent in the recipient’s morning',
    body: 'Delivery time is set in a real timezone, so a 7:00 send is 7:00 where the reader is — not whenever the warehouse in another region finished loading.',
  },
  {
    title: 'Reaches the people without seats',
    body: 'Plant managers, brokers and executives who will never open a BI tool still get the numbers, in the inbox they already read every morning.',
  },
]
